import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, doc, updateDoc, serverTimestamp, increment, addDoc, getDoc } from "firebase/firestore";

const sectionLabels = {
  entrance: "🚪 Entrance",
  section1: "Boss",
  section2: "Boss / Dunhill / Mont-Blanc",
  section3: "Paul Smith",
  section4: "Paul Smith",
  section5: "Starck / Tiffany & Co. / Oakley / Tom Ford / Bobbi Brown / Stella Mccartney / McQueen / Ray-Ban",
  section6: "Persol",
  section7: "Prada / Miu Miu / Tiffany & Co.",
  section8: "Cartier / Brunello Cucinelli / Moncler",
  section9: "Chanel",
  section10: "Lindberg",
  section11: "Oliver Peoples",
  section12: "Gigi Studio / Tory Burch",
  section13: "Gigi Studio / Tory Burch",
  section14: "Oliver Peoples",
  section15: "Cartier",
  section16: "Gucci",
  section17: "Saint-Laurent",
};

const layoutMap = {
  top: ["section6", "section5", "section4", "section3", "section2", "section1", "entrance"],
  left: ["section7", "section8", "section9", "section10", "section11"],
  right: ["section17", "section16", "section15", "section14"],
  bottom: ["section12", "section13"],
};

const FrontInventoryView = ({ user }) => {
  const [inventory, setInventory] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);


  // Handle window resize to switch between mobile and desktop view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch inventory data from Firestore
  useEffect(() => {
    const fetchInventory = async () => {
      const snapshot = await getDocs(collection(db, "inventory"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setInventory(data);
    };
    fetchInventory();
  }, []);

  const getTotalFrames = (brands, sectionId) => {
    return inventory
      .filter(
        (i) =>
          i.name &&
          brands
            .map((b) => b.toLowerCase().trim())
            .includes(i.name.toLowerCase().trim()) &&
          i.section === sectionId
      )
      .reduce((sum, item) => sum + (item.visible ?? 0), 0);
  };

  const logInventoryChange = async (user, action, brand, section, change) => {
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();

      console.log("Logging inventory change:", {
        user: fullName || user.email,
        action,
        brand: brand || "Unknown",
        section: section || "Unknown",
        change: change || 0,
      });

      await addDoc(collection(db, "logs"), {
        user: fullName || user.email,
        action,
        brand: brand || "Unknown",
        section: section || "Unknown",
        change: change || 0,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error logging inventory change:", error);
    }
  };

  const renderSectionTiles = (sectionId) => {
    // Skip entrance for mobile section tiles
    if (sectionId === "entrance") {
      return (
        <div
          key="entrance"
          className="border border-zinc-300 p-3 rounded-lg bg-emerald-600 text-center text-xs font-semibold text-white hidden md:block"
        >
          🚪 Entrance
        </div>
      );
    }

    const brands = sectionLabels[sectionId]?.split("/").map((s) => s.trim()) || [];
    const total = getTotalFrames(brands, sectionId);

    return (
      <div
        key={sectionId}
        className="border border-zinc-400 p-3 rounded-lg text-center text-xs cursor-pointer hover:bg-zinc-100 transition"
        onClick={() => setSelectedSection(sectionId)}
      >
        <h2 className="font-semibold mb-1">{sectionLabels[sectionId]}</h2>
        <p className="text-gray-500">{total} frames</p>
      </div>
    );
  };

  // Desktop grid layout
  const renderDesktopLayout = () => (
    <div className="grid grid-cols-8 gap-2 p-4 text-xs">
      {/* Top Row */}
      <div className="col-span-8 grid grid-cols-8 gap-2 col-start-2">
        {layoutMap.top.map((sectionId) =>
          sectionId === "entrance" ? (
            <div
              key="entrance"
              className="border border-zinc-300 p-3 rounded-lg bg-emerald-600 text-center text-xs font-semibold text-white"
            >
              🚪 Entrance
            </div>
          ) : (
            renderSectionTiles(sectionId)
          )
        )}
      </div>

      {/* Left Column */}
      <div className="row-span-5 grid grid-rows-5 gap-2 col-start-1">
        {layoutMap.left.map((sectionId) => renderSectionTiles(sectionId))}
      </div>

      {/* Right Column */}
      <div className="row-span-5 grid grid-rows-5 gap-2 col-start-8">
        {layoutMap.right.map((sectionId) => renderSectionTiles(sectionId))}
      </div>

      {/* Bottom Row */}
      <div className="col-span-8 grid grid-cols-8 gap-2 mt-2 col-start-2">
        {layoutMap.bottom.map((sectionId) => renderSectionTiles(sectionId))}
      </div>
    </div>
  );

  // Mobile button layout
  const renderMobileLayout = () => (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <button
          className="flex-grow py-6 bg-blue-200 rounded-lg text-center text-lg font-bold"
          onClick={() => setSelectedSection("top-sections")}
        >
          Top
        </button>
        <div className="bg-emerald-600 text-white text-xs font-semibold rounded-lg py-6 w-20 text-center">
          🚪 Entrance
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className="flex-grow bg-blue-200 rounded-lg text-center text-lg font-bold h-80"
          onClick={() => setSelectedSection("left-sections")}
        >
          Left
        </button>
        <div className="flex-grow"></div>
        <button
          className="flex-grow bg-blue-200 rounded-lg text-center text-lg font-bold h-80"
          onClick={() => setSelectedSection("right-sections")}
        >
          Right
        </button>
      </div>

      <button
        className="w-full py-6 bg-blue-200 rounded-lg text-center text-lg font-bold"
        onClick={() => setSelectedSection("bottom-sections")}
      >
        Bottom
      </button>
    </div>
  );

  return (
    <div className="relative p-4">
      {/* Render mobile or desktop layout based on screen size */}
      {!selectedSection && (
        isMobile ? renderMobileLayout() : renderDesktopLayout()
      )}

      {/* Section list view for mobile */}
      {selectedSection && selectedSection.endsWith("-sections") && (
        <div className="fixed inset-0 bg-white p-4 z-50 overflow-y-auto">
          <button
            onClick={() => setSelectedSection(null)}
            className="mb-4 px-4 py-2 bg-zinc-700 text-white rounded"
          >
            Close
          </button>
          <h2 className="text-xl font-bold mb-4 capitalize">
            {selectedSection.replace("-sections", "").toUpperCase()}
          </h2>
          <div>
            {layoutMap[selectedSection.replace("-sections", "")]
              .filter((sectionId) => !(isMobile && selectedSection === "top-sections" && sectionId === "entrance"))
              .map((sectionId) => (
                <button
                  key={sectionId}
                  className="block w-full py-2 px-4 mb-2 bg-gray-100 text-gray-800 rounded-lg text-left border border-gray-300 hover:bg-gray-200 transition"
                  onClick={() => setSelectedSection(sectionId)}
                >
                  {sectionLabels[sectionId]}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Individual section detail view for both mobile and desktop */}
      {selectedSection && !selectedSection.endsWith("-sections") && (
        <div className="fixed inset-0 bg-white p-4 z-50 overflow-y-auto">
          <button
            onClick={() => setSelectedSection(null)}
            className="mb-4 px-4 py-2 bg-zinc-700 text-white rounded"
          >
            Close
          </button>
          <h2 className="text-xl font-bold mb-4 capitalize">
            {sectionLabels[selectedSection]}
          </h2>
          <table className="w-full border text-sm">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-2 text-left">Brand</th>
                <th className="p-2">Visible</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventory
                .filter((item) => item.section === selectedSection)
                .map((item) => (
                  <tr key={item.id} className="border-t even:bg-gray-50">
                    <td className="p-2">{item.name}</td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        className="border p-1 w-20 text-center"
                        value={item.visible}
                        onChange={(e) => {
                          const newVisible = parseInt(e.target.value);
                          if (!isNaN(newVisible) && newVisible >= 0) {
                            const change = newVisible - (item.visible ?? 0);
                            const section = selectedSection || "Unknown";
                            const brand = item.name || "Unknown";

                            updateDoc(doc(db, "inventory", item.id), {
                              visible: newVisible,
                              lastUpdated: serverTimestamp(),
                            });

                            setInventory((prev) =>
                              prev.map((i) =>
                                i.id === item.id ? { ...i, visible: newVisible } : i
                              )
                            );

                            logInventoryChange(user, "Inventory Updated", brand, section, change);
                          }
                        }}
                        onBlur={(e) => {
                          const finalVal = parseInt(e.target.value);
                          if (!isNaN(finalVal) && finalVal >= 0) {
                            const change = finalVal - (item.visible ?? 0);
                            updateDoc(doc(db, "inventory", item.id), {
                              visible: finalVal,
                              lastUpdated: serverTimestamp(),
                            });
                            setInventory((prev) =>
                              prev.map((i) =>
                                i.id === item.id ? { ...i, visible: finalVal } : i
                              )
                            );
                            logInventoryChange(user, "Inventory Updated", item.name, selectedSection, change);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <button
                        disabled={(item.visible ?? 0) <= 0}
                        onClick={() => {
                          if ((item.visible ?? 0) <= 0) return;
                          setSelectedItem({ ...item, model: "", reason: "" }); // Initialize model/reason
                          setShowModal(true);
                        }}
                        className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
                      >
                        Put on Hold
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
            <h2 className="text-lg font-semibold mb-4">
              Place {selectedItem.name} on Hold
            </h2>

            <label className="block mb-2">
              Model (optional):
              <input
                type="text"
                value={selectedItem.model || ""}
                onChange={(e) =>
                  setSelectedItem((prev) => ({
                    ...prev,
                    model: e.target.value,
                  }))
                }
                className="w-full border p-2 rounded mb-3"
                placeholder="Enter model"
              />
            </label>

            <label className="block mb-2">
              Reason (optional):
              <textarea
                value={selectedItem.reason || ""}
                onChange={(e) =>
                  setSelectedItem((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                className="w-full border p-2 rounded mb-3"
                placeholder="Enter reason"
              />
            </label>

            <button
              onClick={async () => {
                const brand = selectedItem.name || "Unknown";
                const section = selectedSection || "Unknown";

                const userDoc = await getDoc(doc(db, "users", user.uid));
                const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();
                const firstName = `${userDoc.data()?.firstName || ""}`.trim();

                // Create onHold document
                const holdDocRef = await addDoc(collection(db, "onHold"), {
                  originalItemId: selectedItem.id,
                  originalSection: section,
                  brand: brand,
                  model: selectedItem.model?.trim() || "Unknown",
                  reason: selectedItem.reason?.trim() || "No reason provided",
                  holdBy: firstName || user.email,
                  holdDate: serverTimestamp(),
                  lastUpdated: serverTimestamp(),
                });

                // Add the initial comment for model and reason
                if (selectedItem.model?.trim() || selectedItem.reason?.trim()) {
                  await addDoc(collection(db, "onHold", holdDocRef.id, "comments"), {
                    user: fullName || user.email,
                    message: `Model: ${selectedItem.model?.trim() || "Unknown"}, Reason: ${selectedItem.reason?.trim() || "No reason provided"}`,
                    timestamp: serverTimestamp(),
                  });
                }

                // Log the hold action
                await addDoc(collection(db, "logs"), {
                  user: fullName || user.email,
                  action: "Put on Hold",
                  brand: brand,
                  section: section,
                  model: selectedItem.model?.trim() || "Unknown",
                  reason: selectedItem.reason?.trim() || "No reason provided",
                  timestamp: serverTimestamp(),
                });

                // Update the inventory item in Firestore
                const ref = doc(db, "inventory", selectedItem.id);
                await updateDoc(ref, {
                  visible: (selectedItem.visible ?? 0) - 1,
                  lastUpdated: serverTimestamp(),
                });

                // Update local state
                setInventory((prev) =>
                  prev.map((i) =>
                    i.id === selectedItem.id
                      ? { ...i, visible: (i.visible ?? 0) - 1 }
                      : i
                  )
                );

                console.log(`✅ Item ${brand} placed on hold by ${fullName}`);
                setShowModal(false);
              }}
              className="bg-green-600 text-white py-2 px-4 rounded w-full mb-2"
            >
              Confirm Hold
            </button>

            <button
              onClick={() => setShowModal(false)}
              className="bg-gray-500 text-white py-2 px-4 rounded w-full"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrontInventoryView;
